import Image from "next/legacy/image";
import React, { useRef } from "react";
import { Container, MarginContainer } from "styles/common/container";
import { Spacer } from "styles/common/spacer";
import { getFeatureFlag } from "utils/utils";
import {
  CardCarousel,
  ClassDetailModal,
  OngoingClassCard,
  UpcomingClassCard
} from "..";
import { ClassJoinModal } from "../class-join-modal/class-join-modal";
import { IPaidUserLandingProps } from "./paid-user-landing.types";
import { PastClassesBlock } from "../past-classes-block/past-classes-block";
import { flags } from "constants/flags";
import { ClassSessionType } from "../hooks/use-user-landing/use-user-landing.types";
import {
  ClassSession,
  SubscriptionType
} from "services/api/sessions/sessionService.types";
import { useUserLanding } from "../hooks/use-user-landing/use-user-landing";
import { KelasLiveCMB } from "../kelas-live-call-me-back/kelas-live-cmb";
import { OnGoingClassCardTitle } from "../ongoing-class-card/ongoing-class-card.styles";
import RateClasses from "../rate-classes/rate-classes";
import {
  KelasLiveOngoingContainer,
  KelasLiveSectionContainer,
  PaidUserStudentHeading,
  PaidUserWeeklySchedule,
  PaidUserWeeklyScheduleButton
} from "./paid-user-mobile.styles";

import KelasLiveSectionOngoing from "public/assets/kelas-live/kelas-live-section-ongoing.svg";
import { BimbelProIcon } from "../assets/bimbel-pro";
import { NewPaidUserFirstClassDoneModal } from "../new-paid-user-bimbel-modal/new-paid-user-first-class-done-modal";
import { Arrow } from "components/icons/arrow";
import { UserNotice } from "../user-notice/user-notice";
import { UpcomingClassHeroSection } from "./components/upcoming-class-hero-section/upcoming-class-hero-section";
import { ClassScheduleCard } from "../class-schedule/class-schedule-card";
import BirdTooltipOverlay from "./components/class-schedule-tooltip/bird-tooltip-overlay";
import useClassScheduleTooltip from "../hooks/use-user-landing/use-class-schedule-tooltip";
import ClassScheduleTooltip from "./components/class-schedule-tooltip/class-schedule-tooltip";
import JadwalChangesInfoFromFeature from "components/jadwal/jadwal-changes-info/jadwal-changes-info-from-feature";
import { usePastClasses } from "hooks/use-live-classes/use-past-classes";
import { getClassType } from "../utils";
import { getClassCardTitle } from "../utils";

const PaidUserLandingLaptop: React.FC<IPaidUserLandingProps> = ({
  primaryUser,
  userSubscriptionStatus
}) => {
  const {
    getJoinLinkURL,
    onGoingClasses,
    upcomingClasses,
    modalClassSession,
    setClassSessionModalDetails,
    modalType,
    showJoiningClassPopUp,
    mediaBaseUrl,
    currentTimeStamp,
    routeToWeeklySchedulePage,
    isFetchingSessions,
    openQnALink,
    modalConfiguration,
    updateModalConfigurationThankyouScreen,
    classSchedule
  } = useUserLanding(primaryUser, userSubscriptionStatus);

  const slotOptInDate = modalConfiguration.api.slot_opt_in_date;
  const { pastSessions, isPastSessionsLoading } = usePastClasses(primaryUser);

  const classScheduleRef = useRef<HTMLDivElement | null>(null);

  const {
    isBirdTooltipVisible,
    isClassScheduleTooltipVisible,
    handleBirdTooltipSkip,
    handleBirdTooltipCheckNow,
    handleClassScheduleTooltipClick
  } = useClassScheduleTooltip(classSchedule, classScheduleRef);

  return (
    <main>
      {isBirdTooltipVisible && !isFetchingSessions && (
        <BirdTooltipOverlay
          isVisible={isBirdTooltipVisible}
          onSkip={handleBirdTooltipSkip}
          onCheckNow={handleBirdTooltipCheckNow}
        />
      )}
      <UserNotice
        primaryUser={primaryUser}
        subscriptionStatus={userSubscriptionStatus}
      />
      {onGoingClasses?.length === 0 &&
        upcomingClasses &&
        (upcomingClasses?.mandatoryClasses.length > 0 ||
          upcomingClasses?.extraClasses.length > 0) &&
        mediaBaseUrl &&
        currentTimeStamp && (
          <UpcomingClassHeroSection
            primaryUser={primaryUser}
            modalConfiguration={modalConfiguration}
            upcomingClasses={upcomingClasses}
            currentTimestamp={currentTimeStamp}
            mediaBaseUrl={mediaBaseUrl}
            setClassSessionModalDetails={setClassSessionModalDetails}
          />
        )}

      {mediaBaseUrl &&
        currentTimeStamp &&
        onGoingClasses &&
        onGoingClasses?.length > 0 && (
          <>
            <KelasLiveSectionContainer>
              <Image
                src={KelasLiveSectionOngoing}
                alt={"ongoing class image"}
                layout="responsive"
              />
              <Container>
                <KelasLiveOngoingContainer>
                  <BimbelProIcon />

                  <PaidUserStudentHeading>
                    {primaryUser.full_name}
                  </PaidUserStudentHeading>
                  <CardCarousel
                    title={
                      <OnGoingClassCardTitle>
                        Lagi berlangsung
                      </OnGoingClassCardTitle>
                    }
                    onScrollToPrevPage={() => null}
                    onScrollToNextPage={() => null}
                    subHeading={
                      userSubscriptionStatus?.subscriptionType ===
                        SubscriptionType.PAID && (
                        <KelasLiveCMB onGoingClassExists />
                      )
                    }
                  >
                    {onGoingClasses?.map(
                      (classSession: ClassSession) =>
                        !isPastSessionsLoading && (
                          <OngoingClassCard
                            subscriptionStatus={userSubscriptionStatus}
                            key={classSession.id}
                            classSession={classSession}
                            baseMediaURL={mediaBaseUrl}
                            currentTimeStamp={currentTimeStamp}
                            primaryUser={primaryUser}
                            isHeroSection
                            handleShowMoreDetails={() =>
                              setClassSessionModalDetails(
                                classSession,
                                ClassSessionType.ONGOING
                              )
                            }
                            handleJoinClass={() =>
                              getJoinLinkURL(classSession.id)
                            }
                            handleJoinQnA={() => openQnALink(classSession.id)}
                            tag={{
                              cardType: getClassType(
                                classSession,
                                slotOptInDate,
                                pastSessions || []
                              ),
                              title: getClassCardTitle(
                                getClassType(
                                  classSession,
                                  slotOptInDate,
                                  pastSessions || []
                                ),
                                true
                              )
                            }}
                          />
                        )
                    )}
                  </CardCarousel>
                </KelasLiveOngoingContainer>
              </Container>
            </KelasLiveSectionContainer>
          </>
        )}
      <MarginContainer>
        <RateClasses
          primaryUser={primaryUser}
          showLatestPendingReviewModal={
            !isFetchingSessions &&
            (!onGoingClasses || onGoingClasses.length === 0)
          }
        />
      </MarginContainer>
      {isClassScheduleTooltipVisible && (
        <ClassScheduleTooltip onClick={handleClassScheduleTooltipClick} />
      )}
      {classSchedule && classSchedule.length > 0 && (
        <div
          className="class-carousel"
          style={{
            backgroundColor: "white",
            ...(isClassScheduleTooltipVisible && {
              zIndex: 1000,
              position: "relative"
            })
          }}
          ref={classScheduleRef}
        >
          <MarginContainer>
            <JadwalChangesInfoFromFeature />
            <CardCarousel
              title="Jadwal Kelasmu"
              onScrollToPrevPage={() => null}
              onScrollToNextPage={() => null}
            >
              {classSchedule.map((schedule) => (
                <ClassScheduleCard key={schedule.id} schedule={schedule} />
              ))}
            </CardCarousel>
            <PaidUserWeeklySchedule>
              <PaidUserWeeklyScheduleButton
                onClick={routeToWeeklySchedulePage}
                variant={"secondary"}
              >
                Cek semua jadwal <Arrow />
              </PaidUserWeeklyScheduleButton>
            </PaidUserWeeklySchedule>
          </MarginContainer>
        </div>
      )}
      {upcomingClasses &&
        upcomingClasses.mandatoryClasses.length > 0 &&
        mediaBaseUrl &&
        currentTimeStamp && (
          <MarginContainer>
            <CardCarousel
              title="Kelas wajib yang akan datang"
              onScrollToPrevPage={() => null}
              onScrollToNextPage={() => null}
            >
              {upcomingClasses.mandatoryClasses?.map(
                (classSession: ClassSession) => (
                  <UpcomingClassCard
                    key={classSession.id}
                    classSession={classSession}
                    baseMediaURL={mediaBaseUrl}
                    currentTimeStamp={currentTimeStamp}
                    handleShowMoreDetails={() =>
                      setClassSessionModalDetails(
                        classSession,
                        ClassSessionType.UPCOMING
                      )
                    }
                  />
                )
              )}
            </CardCarousel>

            <PaidUserWeeklySchedule>
              <PaidUserWeeklyScheduleButton
                onClick={routeToWeeklySchedulePage}
                variant={"secondary"}
              >
                Cek jadwal di minggu lain <Arrow />
              </PaidUserWeeklyScheduleButton>
            </PaidUserWeeklySchedule>
          </MarginContainer>
        )}
      <Spacer token={[16, 16, 80]} />
      {upcomingClasses &&
        upcomingClasses.extraClasses.length > 0 &&
        mediaBaseUrl &&
        currentTimeStamp && (
          <MarginContainer>
            <CardCarousel
              title="Kelas ekstra yang akan datang"
              onScrollToPrevPage={() => null}
              onScrollToNextPage={() => null}
            >
              {upcomingClasses.extraClasses?.map(
                (classSession: ClassSession) => (
                  <UpcomingClassCard
                    key={classSession.id}
                    classSession={classSession}
                    baseMediaURL={mediaBaseUrl}
                    currentTimeStamp={currentTimeStamp}
                    handleShowMoreDetails={() =>
                      setClassSessionModalDetails(
                        classSession,
                        ClassSessionType.UPCOMING
                      )
                    }
                  />
                )
              )}
            </CardCarousel>
          </MarginContainer>
        )}
      <Spacer token={[16, 16, 80]} />
      {getFeatureFlag(flags.showPastSessionsBlock) && (
        <MarginContainer>
          <PastClassesBlock
            primaryUser={primaryUser}
            subscriptionStatus={userSubscriptionStatus}
            routeToWeeklySchedulePage={routeToWeeklySchedulePage}
          />
        </MarginContainer>
      )}

      {mediaBaseUrl && currentTimeStamp && modalClassSession && modalType && (
        <ClassDetailModal
          subscriptionStatus={userSubscriptionStatus}
          classSession={modalClassSession}
          baseMediaURL={mediaBaseUrl}
          currentTimeStamp={currentTimeStamp}
          classType={modalType}
          handleClose={() => setClassSessionModalDetails(null, null)}
          handleJoinClass={() => getJoinLinkURL(modalClassSession.id)}
          primaryUser={primaryUser}
        />
      )}
      <Spacer token={[8, 8, 40]} />
      {showJoiningClassPopUp && (
        <ClassJoinModal
          primaryUser={primaryUser}
          subscriptionType={userSubscriptionStatus.subscriptionType}
        />
      )}

      <NewPaidUserFirstClassDoneModal
        isOpen={modalConfiguration.state.show_thank_you_modal}
        primaryUser={primaryUser}
        onClose={() => updateModalConfigurationThankyouScreen()}
      />
    </main>
  );
};

export { PaidUserLandingLaptop };
